// External modules
import { isFunction } from '@checkout/react-components';

// Internal modules
import { appStore } from '@/store';
import { apolloClient, apolloMutation } from '@/lib/apollo';
import {
  onAfterOnboardAccountCreationHandler,
  onAfterOnboardAccountSuccessHandler,
  onAfterPaymentPlanCreationHandler,
  onAfterPaymentPlanSuccessHandler,
} from './responseHandlers';
import { serializeFormData, serializeOnboardingPlanData } from '../utils';
import { pomaLogout } from '../../PasswordlessOnboarding/utils';
import {
  APPROVE_ONBOARD_PAYMENT_MUTATION,
  COMPLETE_3DS_CONFIRMATION_MUTATION,
  CREATE_ONBOARDING_PAYMENT_PLAN_MUTATION,
  ONBOARD_GUEST_MUTATION,
  SIGN_UP_NEW_MEMBER_MUTATION,
} from './mutations';
import {
  features,
  flushLogsAndRedirect,
  getToken,
  postToMerchantForOldIncontext,
  redirectToMerchantOnSuccess,
} from '@/lib';
import { $logger } from '@/lib/logger';
import { logOnboardSuccessToFpti } from '../fpti';

/**
 * @function approvePayment
 * @description This function works as a helper function to approve a
 * given checkout session and redirect back to the merchant if successful
 * @return null
 */
const approvePayment = () => {
  const token = getToken();
  const { api: apiState } = appStore.getState();
  const intent = apiState.cartDetails.intent;
  const instrumentId = apiState.instrumentId;
  const isBillingAgreement = !!features.get('billingType');

  return apolloClient
    .mutate({
      mutation: APPROVE_ONBOARD_PAYMENT_MUTATION,
      variables: { instrumentId, isBillingAgreement, token },
    })
    .then(response => {
      if (response?.errors?.length) {
        $logger.error('approve_guest_sign_up_payment_failure', {
          errors: response.errors,
        });
        throw new Error(response.errors[0]);
      }

      const { buyer, cart } = response.data?.approveGuestSignUpPayment || {};
      const returnUrl = cart?.returnUrl?.href;
      if (!returnUrl) {
        $logger.warn('approve_payment_return_url_missing');
      }

      if (isFunction(window.xprops?.onAuthorize)) {
        const authorizeData = {
          intent: intent?.toLowerCase(),
          orderID: token,
          payerID: buyer?.userId, // Payer ID of the buyer. Required for onAuthorize - is otherwise optional.
          paymentID: cart?.paymentId, // Payment ID of the transaction. Only present if created via the payments API.
          paymentToken: token,
          returnUrl,
        };

        if (isFunction(window.xprops?.getConnectURL)) {
          return window.xprops
            .getConnectURL({ payerID: buyer?.userId })
            .then(connectURL => {
              return flushLogsAndRedirect({ url: connectURL });
            })
            .catch(err => {
              $logger.error('xprops_getconnecturl_error', {
                err: err.stack || err.toString(),
              });
              return window.xprops.onAuthorize(authorizeData);
            });
        }

        $logger.info('xprops_on_authorize', authorizeData);
        $logger.fpti('xprops_on_authorize', {
          state_name: 'billing',
          transition_name: 'process_on_authorize',
        });
        window.xprops.onAuthorize(authorizeData);
        return;
      }

      // For old checkout.js integration send post message
      const didPostForIncontext = postToMerchantForOldIncontext(returnUrl);
      if (didPostForIncontext) {
        $logger.fpti('xprops_legacy_authorize', {
          state_name: 'billing',
          transition_name: 'process_legacy_on_authorize',
        });
        return;
      }

      /**
       * Logging out for POMA because the access token dropped during passive
       * login has an 8 hour expiration, so we want to remove all cookies
       * except refresh token, which hermes does RT to AT exchange and gets
       * a limited AccessToken
       */
      const { value: onboardOption } = appStore.getState().form.onboardOption;
      const isSignup = onboardOption === 'signup';
      const hasReturnUrl = Boolean(returnUrl);
      if (features.isActive('isPOMAEligible') && isSignup) {
        return pomaLogout().then(() => {
          // Redirect to merchant page
          $logger.info('attempting_redirect_to_merchant_after_poma_logout', {
            hasReturnUrl,
          });
          return redirectToMerchantOnSuccess(returnUrl);
        });
      }

      // Redirect to merchant page
      $logger.info('attempting_redirect_to_merchant', { hasReturnUrl });
      return redirectToMerchantOnSuccess(returnUrl);
    });
};

/**
 * @function complete3DS
 * @param {string} threeDSToken returned from cardinal
 * @description This function is dropped in the window after successful member
 * signup and called by the 3ds iframe via POSTing to our /process3ds route.
 * If call is successful we will attempt to approve the guest payment
 * and redirect back to merchant for payment processing
 */
const complete3DS = thirdPartyToken => {
  return apolloClient
    .mutate({
      mutation: COMPLETE_3DS_CONFIRMATION_MUTATION,
      variables: {
        thirdPartyToken,
        token: getToken(),
      },
    })
    .then(response => {
      if (response?.errors?.length) {
        $logger.error('confirm_3ds_error', response);
        throw new Error(response.errors[0]);
      }

      const { data } = response;
      if (!data.completeThreeDSConfirmation?.flags?.is3DSecureRequired) {
        // Successfully completed three ds challenge
        $logger.info('complete_3ds_success');
        return approvePayment();
      }

      // Handle 3ds challenge failure
      $logger.error('complete_3ds_error');
      throw new Error('WEASLEY_3DS_ERROR');
    });
};

/**
 * @function onboardAction
 * @param {object} data
 * @returns {promise}
 */
const onboardAction = data => {
  const { form: formState } = appStore.getState();
  const { value: onboardOption } = formState.onboardOption;
  const isPOMAEligible = features.isActive('isPOMAEligible');
  const isGuest = onboardOption === 'guest';
  const logOnboardType = isPOMAEligible && !isGuest ? 'poma' : onboardOption;
  const fptiPerfLogStart = Date.now();

  $logger.info(`onboard_${logOnboardType}_init`);

  const variables = serializeFormData(data);
  const mutation = isGuest
    ? ONBOARD_GUEST_MUTATION
    : SIGN_UP_NEW_MEMBER_MUTATION;

  return apolloMutation({ mutation, variables })
    .then(onAfterOnboardAccountCreationHandler)
    .then(data => {
      logOnboardSuccessToFpti(onboardOption, fptiPerfLogStart);
      return onAfterOnboardAccountSuccessHandler(data);
    });
};

/**
 * @function createOnboardingPaymentPlan
 * @param {object} data
 * @returns {promise}
 */
const createOnboardingPaymentPlan = data => {
  const variables = serializeOnboardingPlanData(data);

  return apolloClient
    .mutate({
      mutation: CREATE_ONBOARDING_PAYMENT_PLAN_MUTATION,
      variables,
    })
    .then(onAfterPaymentPlanCreationHandler)
    .then(onAfterPaymentPlanSuccessHandler);
};

export {
  approvePayment,
  complete3DS,
  createOnboardingPaymentPlan,
  onboardAction,
};
